import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ClipboardCheck, MapPin, Package, Trash2, Search, AlertTriangle, ChevronRight, Loader2, RotateCcw, MoveRight, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StocktakingPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationProducts, setLocationProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showZeroAllDialog, setShowZeroAllDialog] = useState(false);
  const [showMoveAllDialog, setShowMoveAllDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [zeroingAll, setZeroingAll] = useState(false);
  const [movingAll, setMovingAll] = useState(false);
  const [destinationLocation, setDestinationLocation] = useState("");
  const [openDestinationPopover, setOpenDestinationPopover] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = locations.filter(loc => 
        loc.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations(locations);
    }
  }, [searchQuery, locations]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const [inventoryRes, allLocsRes] = await Promise.all([
        axios.get(`${API}/inventory/locations`),
        axios.get(`${API}/locations`)
      ]);
      const stockLocs = inventoryRes.data || [];
      const masterLocs = allLocsRes.data || [];
      // Show ALL defined locations (master list) UNIONed with any location that
      // currently holds stock — so empty locations stay visible and inventoriable.
      const seen = new Map();
      [...masterLocs, ...stockLocs].forEach((loc) => {
        if (!loc || !loc.trim()) return;
        const key = loc.trim().toLowerCase();
        if (!seen.has(key)) seen.set(key, loc.trim());
      });
      const union = [...seen.values()].sort((a, b) => a.localeCompare(b, "sv"));
      setLocations(union);
      setFilteredLocations(union);
      setAllLocations(masterLocs);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Kunde inte ladda lagerplatser");
    } finally {
      setLoading(false);
    }
  };

  const selectLocation = async (location) => {
    setSelectedLocation(location);
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/inventory/location/${encodeURIComponent(location)}`);
      setLocationProducts(response.data.items || []);
    } catch (error) {
      console.error("Error fetching location products:", error);
      toast.error("Kunde inte ladda produkter för lagerplatsen");
      setLocationProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleClearLocation = async () => {
    if (!selectedLocation) return;
    
    setClearing(true);
    try {
      const response = await axios.post(`${API}/inventory/location/${encodeURIComponent(selectedLocation)}/clear`);
      toast.success(response.data.message || "Lagerplatsen tömd!");
      setShowConfirmDialog(false);
      
      // Navigate to scan page with location pre-filled
      navigate(`/scan?location=${encodeURIComponent(selectedLocation)}`);
    } catch (error) {
      console.error("Error clearing location:", error);
      toast.error("Kunde inte tömma lagerplatsen: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const handleZeroAllInventory = async () => {
    setZeroingAll(true);
    try {
      const response = await axios.post(`${API}/inventory/zero-all-inventory`);
      toast.success(`${response.data.cleared_count} produkter nollställda (${response.data.cleared_quantity} st totalt)`);
      setShowZeroAllDialog(false);
      // Refresh locations
      fetchLocations();
    } catch (error) {
      console.error("Error zeroing inventory:", error);
      toast.error(error.response?.data?.detail || "Kunde inte nollställa lagret");
    } finally {
      setZeroingAll(false);
    }
  };

  const handleMoveAllFromLocation = async () => {
    if (!selectedLocation || !destinationLocation) {
      toast.error("Välj en destinationslagerplats");
      return;
    }
    
    setMovingAll(true);
    try {
      const response = await axios.post(
        `${API}/inventory/location/${encodeURIComponent(selectedLocation)}/move-all`,
        { destination: destinationLocation }
      );
      toast.success(response.data.message);
      setShowMoveAllDialog(false);
      setDestinationLocation("");
      // Refresh the location products
      selectLocation(selectedLocation);
      // Also refresh locations list
      fetchLocations();
    } catch (error) {
      console.error("Error moving products:", error);
      toast.error(error.response?.data?.detail || "Kunde inte flytta produkterna");
    } finally {
      setMovingAll(false);
    }
  };

  const totalQuantity = locationProducts.reduce((sum, p) => sum + (p.antal || 0), 0);

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <ClipboardCheck className="h-6 w-6 text-primary" />
                <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="stocktaking-title">
                  Inventering
                </h1>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Välj en lagerplats för att inventera
              </p>
            </div>
            {isAdmin && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowZeroAllDialog(true)}
                className="gap-2"
                data-testid="zero-all-btn"
              >
                <RotateCcw className="h-4 w-4" />
                Nollställ allt lager
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {!selectedLocation ? (
          /* Location Selection View */
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                placeholder="Sök lagerplats..."
                className="pl-10 font-mono"
                data-testid="location-search"
                autoFocus
              />
            </div>

            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                Laddar lagerplatser...
              </div>
            ) : filteredLocations.length === 0 ? (
              <Card className="p-12 text-center">
                <MapPin className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-xl font-bold mb-2">
                  {searchQuery ? "Ingen lagerplats hittades" : "Inga lagerplatser"}
                </h3>
                <p className="text-muted-foreground">
                  {searchQuery ? "Prova ett annat sökord" : "Det finns inga lagerplatser i systemet"}
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="location-grid">
                {filteredLocations.map((location) => (
                  <Card
                    key={location}
                    className="cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => selectLocation(location)}
                    data-testid={`location-${location}`}
                  >
                    <CardContent className="p-4 text-center">
                      <MapPin className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                      <p className="font-mono font-bold">{location}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Location Detail View */
          <div className="space-y-4">
            {/* Back button and location header */}
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSelectedLocation(null);
                  setLocationProducts([]);
                }}
                className="gap-2"
              >
                ← Tillbaka
              </Button>
            </div>

            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MapPin className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-bold font-mono" data-testid="selected-location">
                        {selectedLocation}
                      </h2>
                      <p className="text-muted-foreground">
                        {locationProducts.length} produkt{locationProducts.length !== 1 ? "er" : ""} • {totalQuantity} st totalt
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {locationProducts.length > 0 && (
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => setShowMoveAllDialog(true)}
                        className="gap-2"
                        data-testid="move-all-btn"
                      >
                        <MoveRight className="h-5 w-5" />
                        Flytta alla produkter
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="lg"
                      onClick={() => setShowConfirmDialog(true)}
                      className="gap-2"
                      data-testid="clear-location-btn"
                    >
                      <Trash2 className="h-5 w-5" />
                      Töm & Inventera
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products at this location */}
            <div>
              <h3 className="font-heading text-lg font-bold mb-3">
                Produkter på lagerplatsen
              </h3>
              
              {loadingProducts ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  Laddar produkter...
                </div>
              ) : locationProducts.length === 0 ? (
                <Card className="p-8 text-center">
                  <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="font-heading text-lg font-bold mb-2">Tomt på lagerplatsen</h3>
                  <p className="text-muted-foreground mb-4">
                    Det finns inga produkter registrerade här
                  </p>
                  <Button onClick={() => navigate(`/scan?location=${encodeURIComponent(selectedLocation)}`)}>
                    Inleverans
                  </Button>
                </Card>
              ) : (
                <div className="space-y-2" data-testid="location-products">
                  {locationProducts.map((product) => (
                    <Card 
                      key={product.id || product.ean} 
                      className="cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => navigate(`/inventory/${product.ean}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-heading font-bold">
                                {product.artikelnummer || product.ean}
                              </span>
                              <Badge variant="outline" className="font-mono">
                                {product.antal} st
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {product.produktnamn}
                            </p>
                            {product.ean && (
                              <p className="text-xs text-muted-foreground font-mono mt-1">
                                EAN: {product.ean}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Confirm Clear Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg mx-auto" data-testid="confirm-clear-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
              Bekräfta tömning
            </DialogTitle>
            <DialogDescription className="text-sm">
              Töm lagerplats <span className="font-mono font-bold">{selectedLocation}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2 sm:py-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 sm:p-4">
              <p className="text-sm font-medium text-destructive mb-2">
                Detta kommer att:
              </p>
              <ul className="text-xs sm:text-sm text-muted-foreground space-y-1">
                <li>• Ta bort {locationProducts.length} produkt{locationProducts.length !== 1 ? "er" : ""} ({totalQuantity} st)</li>
                <li>• Logga som "INVENTERING"</li>
                <li>• Omdirigera till skanning</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
            <Button 
              variant="destructive" 
              onClick={handleClearLocation}
              disabled={clearing}
              className="w-full sm:w-auto order-1 sm:order-2"
              data-testid="confirm-clear-btn"
            >
              {clearing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Tömmer...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Töm lagerplats
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)} 
              disabled={clearing}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Avbryt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zero All Inventory Dialog - Admin Only */}
      <Dialog open={showZeroAllDialog} onOpenChange={setShowZeroAllDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg mx-auto" data-testid="zero-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0" />
              Nollställ allt lager
            </DialogTitle>
            <DialogDescription className="text-sm">
              Detta kommer att sätta alla lagersaldon till 0
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2 sm:py-4">
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 sm:p-4">
              <p className="text-sm font-medium text-destructive mb-2">
                VARNING - Denna åtgärd kan inte ångras!
              </p>
              <ul className="text-xs sm:text-sm text-muted-foreground space-y-1">
                <li>• Alla produkters lagersaldo sätts till 0</li>
                <li>• Produkterna finns kvar i systemet</li>
                <li>• Åtgärden loggas i historiken</li>
                <li>• Skanna sedan in produkter på nytt</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
            <Button 
              variant="destructive" 
              onClick={handleZeroAllInventory}
              disabled={zeroingAll}
              className="w-full sm:w-auto order-1 sm:order-2"
              data-testid="confirm-zero-all-btn"
            >
              {zeroingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Nollställer...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Nollställ allt
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowZeroAllDialog(false)} 
              disabled={zeroingAll}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Avbryt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move All Products Dialog */}
      <Dialog open={showMoveAllDialog} onOpenChange={(open) => {
        setShowMoveAllDialog(open);
        if (!open) {
          setDestinationLocation("");
          setOpenDestinationPopover(false);
        }
      }}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg mx-auto" data-testid="move-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <MoveRight className="h-5 w-5 text-primary flex-shrink-0" />
              Flytta alla produkter
            </DialogTitle>
            <DialogDescription className="text-sm">
              Flytta alla produkter från <span className="font-mono font-bold">{selectedLocation}</span> till en annan lagerplats
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-muted/50 border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-2">
                Produkter som kommer flyttas:
              </p>
              <p className="font-bold">
                {locationProducts.length} produkt{locationProducts.length !== 1 ? "er" : ""} ({totalQuantity} st totalt)
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Välj destinationslagerplats</label>
              <Popover open={openDestinationPopover} onOpenChange={setOpenDestinationPopover}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openDestinationPopover}
                    className="w-full justify-between font-mono"
                    data-testid="destination-selector"
                  >
                    {destinationLocation || "Välj lagerplats..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Sök lagerplats..." />
                    <CommandList>
                      <CommandEmpty>Ingen lagerplats hittades</CommandEmpty>
                      <CommandGroup>
                        {allLocations
                          .filter(loc => loc !== selectedLocation)
                          .map((loc) => (
                            <CommandItem
                              key={loc}
                              value={loc}
                              onSelect={() => {
                                setDestinationLocation(loc);
                                setOpenDestinationPopover(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", destinationLocation === loc ? "opacity-100" : "opacity-0")} />
                              {loc}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
            <Button 
              onClick={handleMoveAllFromLocation}
              disabled={movingAll || !destinationLocation}
              className="w-full sm:w-auto order-1 sm:order-2 gap-2"
              data-testid="confirm-move-all-btn"
            >
              {movingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Flyttar...
                </>
              ) : (
                <>
                  <MoveRight className="h-4 w-4" />
                  Flytta produkter
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowMoveAllDialog(false)} 
              disabled={movingAll}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Avbryt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
